import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import getStyled from "../../hooks/getStyled";
import SFButton from "../button";

import { Autocomplete, InputAdornment } from "@mui/material";

import SFTextField from "../textField";
import { SFSearchIcon } from "../icons";

import GenericSearchOption from "./genericSearchOption";
import LoadMoreButton from "./loadMoreButton";
import { SFStack } from "../grid";

const { styled } = getStyled();

const SFTextFieldStyled = styled(SFTextField, {
  shouldForwardProp: (prop) =>
    prop !== "hasSearchIcon" && prop !== "shouldLabelShrink",
})(({ theme, hasSearchIcon, label, shouldLabelShrink }) => ({
  "& .MuiInputAdornment-root": {
    // This is for the search icon. It starts vertically centered and when focused
    // and there is a label, it translates down
    position: "absolute",
    top: "11px",
    left: "18px",
    transition: theme.transitions.create(["transform"]),
    ...(label &&
      shouldLabelShrink && {
      transform: "translateY(11px)",
    }),
  },
  "& .MuiInputLabel-root": {
    // This styles the label when there is a search icon. It starts vertically centered
    // at the right of the icon and when focused it translates above it and shrinks
    ...(hasSearchIcon && {
      pointerEvents: "none",
      position: "absolute",
      top: "16px",
      left: "44px",
      transform: "none",
      "&.MuiInputLabel-shrink": {
        transform: "translate(-26px, -10px) scale(0.75)",
      },
    }),
  },
  "& .MuiInputBase-root": {
    // This is to position the input when there is a search icon.
    // When there is no label we remove the top padding so that the input
    // is always centered
    ...(hasSearchIcon && {
      ...(!label && {
        paddingTop: 0,
      }),
      "& .MuiInputBase-input": {
        marginLeft: "34px",
        ...(!label && { height: "39px" }),
      },
    }),
  },
}));

const ListItemStackStyled = styled(SFStack)(({ theme }) => ({
  width: "100%",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: theme.palette.blue50.main,
  },
}));

// This decides how many of the selected values are displayed and how many overflow.
// For example: First option, Second option, +3
// TODO: Make this dynamic so that only the values that do not fit are overflowed
const VISIBLE_VALUES = 2;

// The limit of how many values are displayed starts off at this value
// and when we click the button, it increments by the value of this constant
const LIMIT_STEP = 50;

const SFGenericSearch = ({
  source,
  selectedValues,
  onSelect,
  onClose,
  label,
  placeholder,
  className,
  hasSearchIcon,
  disablePortal,
  multiple,
  fetchData,
  optionRenderer,
  clearOnSelect,
  disabled,
  required,
  loading = false,
  fullWidth = false,
  errors,
  addNewOption = false,
  size = "medium",
  variant = "filled",
  customIcon = null,
  isCopilotField = false,
}) => {
  const { t } = useTranslation();
  const [searchState, setSearchState] = useState({
    // Changing this key resets the autocomplete component. This is useful
    // when using the clearOnSelect property
    key: new Date().toString(),
    results: source ?? [],
    limit: LIMIT_STEP,
    search: "",
    selected: selectedValues,
    focused: false,
  });

  const sortBySelected = (a, b) => {
    const aSelected = searchState.selected.includes(a.value);
    const bSelected = searchState.selected.includes(b.value);
    if (aSelected === bSelected) {
      return 0;
    }
    return aSelected ? -1 : 1;
  };

  const handleResetState = () => {
    setSearchState((prv) => ({
      ...prv,
      key: new Date().toString(),
      selected: [],
    }));
  };

  const handleSearchChange = (value) => {
    setSearchState((prv) => ({
      ...prv,
      search: value.toString(),
    }));
  };

  const handleOpen = () => {
    setSearchState((prv) => ({
      ...prv,
      focused: true,
      ...(multiple && {
        results: prv.results.sort(sortBySelected),
      }),
    }));
  };

  const handleClose = () => {
    if (onClose) {
      onClose(searchState.selected);
    }
    setSearchState((prv) => ({
      ...prv,
      focused: false,
      search: "",
      limit: LIMIT_STEP,
    }));
  };

  const handleBlur = () => {
    setSearchState((prv) => ({
      ...prv,
      focused: false,
    }));
  };

  const handleServerSideSearch = async (value) => {
    let data = await fetchData(value);
    setSearchState((prv) => ({ ...prv, results: data }));
  };

  const handleSelect = (option, checked) => {
    const value = option.value;

    if (!multiple) {
      handleClose();
    }

    if (onSelect) {
      onSelect(option);
    }

    if (clearOnSelect) {
      handleResetState();
      return;
    }

    if (checked) {
      setSearchState((prv) => ({
        ...prv,
        selected: multiple ? [...prv.selected, value] : [value],
      }));
    } else {
      setSearchState((prv) => ({
        ...prv,
        selected: multiple ? prv.selected.filter((x) => x !== value) : [value],
      }));
    }
  };

  useEffect(() => {
    const value = searchState.search;
    if (source) {
      // When the search is client side filter the results based on the search value
      let res = source.filter((x) =>
        x.value?.toLowerCase().includes(value.toLowerCase())
      );
      if (res.length === 0 && addNewOption) {
        res = [
          {
            value: `${t("components.typeahead.add")} "${searchState.search}"`,
            id: "addNewOption",
            val: searchState.search,
          },
        ];
      }
      setSearchState((prv) => ({
        ...prv,
        results: res,
      }));
    }

    if (fetchData) {
      // When the search is server side, make a request to retrieve the values.
      // We are adding a 400ms delay and a 3 characted minimum so that we do not
      // make a request at every character typed
      if (value.trim().length > 2) {
        const delay = setTimeout(() => {
          handleServerSideSearch(value);
        }, 400);

        return () => clearTimeout(delay);
      } else {
        setSearchState((prv) => ({
          ...prv,
          results: [],
        }));
      }
    }
  }, [searchState.search]);

  useEffect(() => {
    // When the supplied source data changes, we reset the results state
    // and sort by the selected values so that they appear on top
    if (source) {
      setSearchState((prv) => ({
        ...prv,
        results: source.sort(sortBySelected),
      }));
    }
  }, [source]);

  useEffect(() => {
    if (source) {
      setSearchState((prv) => ({
        ...prv,
        selected: selectedValues,
      }));
    }
  }, [selectedValues]);

  const value = useMemo(() => {
    // The value that is displayed in the text field. When focused, we display the search value
    // and otherwise we display the selected values comma seperated
    if (searchState.focused) return searchState.search;

    if (searchState.selected.length === 0) return "";

    // This handles the display of the overflow values
    if (searchState.selected.length > VISIBLE_VALUES)
      return `${searchState.selected.slice(0, VISIBLE_VALUES).join(", ")}, +${
        searchState.selected.length - VISIBLE_VALUES
      }`;

    return searchState.selected
      .map((value) => (optionRenderer ? optionRenderer({ value }) : value))
      .join(", ");
  });

  const options = useMemo(() => {
    // These are the displayed options for the dropdown. If they are more
    // than the limit, we add an extra option for the load more button
    if (searchState.results.length <= searchState.limit)
      return searchState.results;

    return [
      ...searchState.results.filter((x, idx) => idx < searchState.limit),
      { value: "loadMoreBtn" },
    ];
  });

  const shouldLabelShrink =
    searchState.focused || searchState.selected.length > 0;

  return (
    <Autocomplete
      key={searchState.key}
      open={!loading && searchState.focused}
      onOpen={handleOpen}
      onClose={handleClose}
      onBlur={handleBlur}
      noOptionsText={t("components.typeahead.resultsPlaceholder")}
      disablePortal={disablePortal}
      disabled={disabled}
      size={size}
      value={value}
      options={options}
      isOptionEqualToValue={(option, value) =>
        option.value?.toLowerCase().includes(searchState.search.toLowerCase())
      }
      getOptionLabel={() => value}
      disableClearable
      clearOnBlur
      fullWidth={fullWidth}
      renderInput={(params) => (
        <SFTextFieldStyled
          {...params}
          errors={errors}
          className={className}
          label={label}
          variant={variant}
          size={size}
          placeholder={placeholder}
          required={required}
          onChange={(e) => handleSearchChange(e.target.value)}
          hasSearchIcon={hasSearchIcon}
          shouldLabelShrink={shouldLabelShrink}
          InputLabelProps={{
            shrink: shouldLabelShrink,
          }}
          isCopilotField={isCopilotField}
          InputProps={{
            ...params.InputProps,
            ...(hasSearchIcon && {
              startAdornment: (
                <InputAdornment position="start">
                  {customIcon ? customIcon : <SFSearchIcon />}
                </InputAdornment>
              ),
            }),
          }}
        />
      )}
      renderOption={(params, option, { inputValue }) => {
        if (option.id === "addNewOption") {
          return (
            <ListItemStackStyled alignItems="flex-start">
              <SFButton
                variant="utility"
                onClick={() =>
                  handleSelect({ value: option.val, id: option.val }, true)
                }
              >
                {option.value}
              </SFButton>
            </ListItemStackStyled>
          );
        }
        if (option.value === "loadMoreBtn")
          return (
            <LoadMoreButton
              key={option.value}
              onClick={() =>
                setSearchState((prv) => ({
                  ...prv,
                  limit: prv.limit + LIMIT_STEP,
                }))
              }
            />
          );

        const displayValue = optionRenderer
          ? optionRenderer(option)
          : option.value;
        const selected = searchState.selected.includes(option.value);
        const onSelect = () => handleSelect(option, !selected);

        return (
          <GenericSearchOption
            key={option.value}
            value={displayValue}
            hasCheckbox={multiple}
            selected={selected}
            onSelect={onSelect}
            inputValue={inputValue}
          />
        );
      }}
    />
  );
};

SFGenericSearch.defaultProps = {
  source: null,
  selectedValues: [],
  onSelect: null,
  onClose: null,
  label: null,
  placeholder: null,
  className: null,
  hasSearchIcon: false,
  disablePortal: false,
  multiple: false,
  fetchData: null,
  optionRenderer: null,
  clearOnSelect: false,
  disabled: false,
  required: false,
  loading: false,
};

SFGenericSearch.propTypes = {
  // The dataset of the generic search. Requires a unique value property.
  // To change what is displayed use the optionRenderer property
  // Do not use simultaneously with the fetchData property
  source: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
    })
  ),
  // The currently selected values. It is an array of the "values" property of the source.
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  // Callback function that gets called when a new item is selected
  onSelect: PropTypes.func,
  // Callback function that gets called when the user closes the dropdown (on blur)
  onClose: PropTypes.func,
  // The label of the text field component
  label: PropTypes.string,
  // The placeholder of the text field component
  placeholder: PropTypes.string,
  // Used for styling. It is required in order to use the getStyled hook
  className: PropTypes.string,
  // Whether the text field has a search icon on the start
  hasSearchIcon: PropTypes.bool,
  // Whether the dropdown is rendered in a portal or locally
  // For use inside a drawer set it to `true`
  disablePortal: PropTypes.bool,
  // Whether the dropdown allows for multiple values to be selected
  multiple: PropTypes.bool,
  // A function that gets called when the search changes and
  // returns the data as an array of objects (required `value` property)
  fetchData: PropTypes.func,
  // A function that gets called with the option object and returns a string to be displayed
  optionRenderer: PropTypes.func,
  // Whether the search clears when selecting an item
  clearOnSelect: PropTypes.bool,
  // Whether the component is disabled
  disabled: PropTypes.bool,
  // Whether the component is required
  required: PropTypes.bool,
  // Whether the component is loading
  // When true the dropdown doesn't open
  loading: PropTypes.bool,
  // Whether the component has fullWidth
  fullWidth: PropTypes.bool,
  // The size of the component
  size: PropTypes.oneOf(["small", "medium"]),
  // The variant of the text field
  variant: PropTypes.oneOf(["filled", "outlined"]),
};

export default SFGenericSearch;
